import { Controller } from '@hotwired/stimulus'
import flatpickr from 'flatpickr'

interface FlatpickrInputElement extends HTMLInputElement {
  _flatpickrInstance?: flatpickr.Instance
}

export default class extends Controller {
  static targets = ['input', 'projectInput']

  declare readonly inputTarget: FlatpickrInputElement

  private initialValue: string | null = null

  connect (): void {
    this.update()
    document.addEventListener('datepicker:update', this.update.bind(this))
    document.addEventListener('datepicker:reset', this.reset.bind(this))
  }

  disconnect (): void {
    document.removeEventListener('datepicker:reset', this.reset.bind(this))
    document.removeEventListener('datepicker:update', this.update.bind(this))
    this.destroyDatepicker(this.inputTarget)
  }

  private reset (): void {
    setTimeout(() => {
      const datepicker = this.inputTarget._flatpickrInstance

      if (datepicker == null) return

      const value = this.initialValue ?? ''

      if (value === '') {
        datepicker.clear(false)
      } else {
        datepicker.setDate(value, false, datepicker.config.dateFormat)
      }
    }, 0)
  }

  update (): void {
    setTimeout(() => {
      const inputTypes = ['date', 'hidden', 'datetime-local', 'datetime', 'time']
      // Handle inputTarget
      if (inputTypes.includes(this.inputTarget.type)) {
        this.initializeDatepicker(this.inputTarget)

        const defaultValue = this.inputTarget.dataset.defaultValue
        if (typeof defaultValue === 'string' && defaultValue !== '') {
          this.inputTarget._flatpickrInstance?.setDate(defaultValue, true)
        }
        if (this.initialValue == null) this.initialValue = this.inputTarget.value
      } else if (this.inputTarget._flatpickrInstance != null) {
        this.inputTarget.removeAttribute('data-original-type')
        this.destroyDatepicker(this.inputTarget)
      }
    }, 10)
  }

  initializeDatepicker (inputElement: FlatpickrInputElement): void {
    const enableTime = inputElement.dataset.datepickerEnableTime === 'true'
    const mode = inputElement.dataset.datepickerMode ?? 'single'
    const isTimeOnly = inputElement.type === 'time'

    inputElement.dataset.originalType = inputElement.type

    let dateFormat = 'Y-m-d'
    let altFormat = this.getDateFormat()

    if (enableTime) {
      dateFormat += ' H:i'
      altFormat += ' H:i'
    }

    if (isTimeOnly) {
      dateFormat = 'H:i'
      altFormat = 'H:i'
    }

    const options: any = {
      enableTime: enableTime || isTimeOnly,
      dateFormat,
      altFormat,
      altInput: true,
      allowInput: true,
      monthSelectorType: 'static',
      altInputClass: `${inputElement.className} flatpickr-input`,
      static: true,
      mode,
      noCalendar: isTimeOnly,
      time_24hr: true,
      now: this.getNow(),
      onOpen: (_selectedDates: Date[], _dateStr: string, instance: flatpickr.Instance): void => {
        const scrollIntoView = (): void => instance.calendarContainer.scrollIntoView({ block: 'nearest' })

        scrollIntoView()
        instance.calendarContainer.addEventListener('animationend', scrollIntoView, { once: true })
      }
    }

    if (mode === 'range') {
      options.minDate = inputElement.min !== '' ? inputElement.min : undefined
      options.maxDate = inputElement.max !== '' ? inputElement.max : undefined
    } else {
      // TODO: need find a better way to show dates in past
      // workaround for ScheduledWorkflows and past valid_until dates
      const hasValue = inputElement.value !== '' && inputElement.value.trim() !== ''
      const hasMin = inputElement.min !== '' && inputElement.min.trim() !== ''
      const hasMax = inputElement.max !== '' && inputElement.max.trim() !== ''
      const valueDate = hasValue ? Date.parse(inputElement.value) : NaN
      const minDate = hasMin ? Date.parse(inputElement.min) : NaN

      // If value is before min, enable that specific date plus dates from min onwards
      if (hasValue && hasMin && !isNaN(valueDate) && !isNaN(minDate) && valueDate < minDate) {
        const enableRange: any = {
          from: inputElement.min,
          to: hasMax ? inputElement.max : '2099-12-31' // Far future date if no max
        }
        options.enable = [inputElement.value, enableRange]
      } else {
        if (hasMin) options.minDate = inputElement.min
        if (hasMax) options.maxDate = inputElement.max
      }
    }

    inputElement._flatpickrInstance = flatpickr(inputElement, options)
  }

  destroyDatepicker (inputElement: FlatpickrInputElement): void {
    const datepicker = inputElement._flatpickrInstance

    if (datepicker != null) {
      datepicker.destroy()
      delete inputElement._flatpickrInstance

      inputElement.value = ''
      inputElement.type = inputElement.dataset.originalType !== undefined ? inputElement.dataset.originalType : 'text'
      inputElement.nextElementSibling?.remove()
    }
  }

  private getDateFormat (): string {
    const metaTag = document.querySelector("meta[name='datepicker-date-format']") as HTMLMetaElement
    return metaTag?.content ?? 'd/m/Y'
  }

  private getNow (): Date | undefined {
    const metaTag = document.querySelector("meta[name='datepicker-now']") as HTMLMetaElement
    if (metaTag?.content == null || metaTag.content === '') return undefined

    const now = new Date(metaTag.content)
    return isNaN(now.getTime()) ? undefined : now
  }
}
